import { ComponentFilter, ValidationMessageOverride } from '../settings/settings';

export function format(first: string, middle: string, last: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

export const asyncTimeout = async (run, timeoutMs) =>
  new Promise(resolve => {
    setTimeout(() => resolve(run()), timeoutMs);
  });

export const awaitDefinedResult = async (evaluate, timeoutMs = 2000) =>
  Promise.race([
    new Promise((_, reject) => {
      setTimeout(reject, timeoutMs);
    }),
    // not ideal, but I couldn't find another way to do it
    /* eslint no-async-promise-executor: off */
    new Promise(async resolve => {
      let result;
      while ([undefined, null].includes(result = evaluate())) {
        // we really do want to wait for each one in series, not parallel
        /* eslint no-await-in-loop: off */
        await asyncTimeout(() => {}, 10);
      }
      resolve(result);
    }),
  ]);

export const clickEventModifierKeys = ['altKey', 'ctrlKey', 'metaKey', 'shiftKey'];

const getConfigForComponent = (
  config: ValidationMessageOverride | ValidationMessageOverride[],
  componentFilter?: ComponentFilter,
) =>
  componentFilter && Array.isArray(config)
    ? config.find(c =>
        Object.keys(componentFilter).every(prop => c?.componentFilter?.[prop] === componentFilter?.[prop]),
      ) ?? config.find(c => c.componentFilter === undefined)
    : (config as ValidationMessageOverride);

const validationMessagePrecedence = [
  'badInput',
  'valueMissing',
  'tooLong',
  'tooShort',
  'typeMismatch',
  'patternMismatch',
  'rangeOverflow',
  'rangeUnderflow',
  'stepMismatch',
];

const replacePropValues = (message, props) => {
  if (!message) return null;
  if (!props) return message;
  return Object.keys(props).reduce(
    (messageValue, propKey) => messageValue.replace(new RegExp(`\\{${propKey}\\}`, 'g'), props?.[propKey]),
    message,
  );
};

export const getOverriddenValidationMessage = (
  validationMessageOverrideConfig: ValidationMessageOverride | ValidationMessageOverride[],
  validityState: ValidityState,
  fallbackMessage: string,
  componentProps?: {
    step?: string | number;
    min?: string | number;
    max?: string | number;
    value?: string | number;
    minLength?: string | number;
    maxLength?: string | number;
    valueLength?: number;
  },
  componentFilter?: ComponentFilter,
) => {
  // custom errors always take precedence
  if (validityState.customError) {
    return fallbackMessage;
  }
  const overrideConfig = getConfigForComponent(validationMessageOverrideConfig, componentFilter);
  for (const validationError of validationMessagePrecedence) {
    if (validityState[validationError]) {
      return replacePropValues(overrideConfig?.[validationError], componentProps) ?? fallbackMessage;
    }
  }
  return fallbackMessage;
};

export const selectFormSubmitters = form => [
  ...(form?.querySelectorAll('[type=submit],button:not([type]),omk-button:not([type])') ?? []),
  ...(form?.id ? document.querySelectorAll(`button[form=${form.id}], omk-button[form=${form.id}]`) : []),
];

export const acceptedKeyboardInteractionCodes = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight'
];

export const acceptedNumberKeyCodes = [
  'Digit0',
  'Digit1',
  'Digit2',
  'Digit3',
  'Digit4',
  'Digit5',
  'Digit6',
  'Digit7',
  'Digit8',
  'Digit9',
  'Numpad0',
  'Numpad1',
  'Numpad2',
  'Numpad3',
  'Numpad4',
  'Numpad5',
  'Numpad6',
  'Numpad7',
  'Numpad8',
  'Numpad9'
];
